<template>
  <div class="map-container">
    <div :id="mapId" class="map" v-if="access_token" />
    <div v-else style="position: absolute: margin: auto; padding: 1rem;">
      Uh oh, we are missing a valid API access token required to use Mapbox
      GL.&nbsp;&nbsp;Please set the VUE_APP_MAPBOX_ACCESS_TOKEN environment
      variable with a valid token.&nbsp;&nbsp;If you don't have a valid token go
      <a
        target="_blank"
        href="https://docs.mapbox.com/help/tutorials/get-started-tokens-api/"
        >here</a
      >
      to create one.
    </div>
  </div>
</template>

<script>
import MapStylesControl from "./MapStylesControl";
import DrawControls from "./DrawControls";
import LayersControl from "./LayersControl";
import DrawTips from "./DrawTips";
import CopyTool from "./copyTool";

import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

export default {
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    polygons: {
      type: Array,
      default: () => [],
    },
    drawPolygons: {
      type: Array,
      default: () => [],
    },
    polygonsSourceName: {
      type: String,
      default: "Polygon",
    },
    zoom: {
      type: Number,
      default: 0,
    },
    minZoom: {
      type: Number,
      default: 0,
    },
    maxZoom: {
      type: Number,
      default: 24,
    },
    mapStyle: {
      type: String,
      default: "mapbox://styles/mapbox/streets-v11",
    },
    logoPosition: {
      type: String,
      default: "bottom-left",
    },
    center: {
      type: Array,
      default: () => [-87.65, 41.85],
    },
    bounds: {
      type: Array,
      default: null,
      // -171.791110603, 18.91619, -66.96466, 71.3577635769
      // [[-73.9876, 40.7661], [-73.9397, 40.8002]]
    },
    boundsOptions: {
      type: Object,
      default: null,
    },
    zoomControl: {
      type: Boolean,
      default: false,
    },
    zoomControlPosition: {
      type: String,
      default: "top-right",
    },
    geocoder: {
      type: Boolean,
      default: false,
    },
    geocoderPosition: {
      type: String,
      default: "top-left",
    },
    geocoderContainerId: {
      type: String,
      default: null,
    },
    focusOnGeocoder: {
      type: Boolean,
      default: false,
    },
    drawTools: {
      type: Boolean,
      default: false,
    },
    drawToolsPosition: {
      type: String,
      default: "top-right",
    },
    drawTipsPosition: {
      type: String,
      default: "bottom-left",
    },
    copyTools: {
      type: Array,
      default: () => [],
    },
    copyToolsPosition: {
      type: String,
      default: "top-right",
    },
    showCoords: {
      type: Boolean,
      default: false,
    },
    showScale: {
      type: Boolean,
      default: false,
    },
    scaleCoordsPosition: {
      type: String,
      default: "bottom-right",
    },
    stylesControl: {
      type: Boolean,
      default: false,
    },
    stylesControlPosition: {
      type: String,
      default: "top-right",
    },
    layersControl: {
      type: Boolean,
      default: false,
    },
    layersControlPosition: {
      type: String,
      default: "bottom-left",
    },
    showAttribution: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mapId: "map",
      map: null,
      access_token: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      customLayers: [], // workaround used internally to keep added layers when switching styles
      customImages: [], // workaround used internally to keep added images when switching styles
      allLayerEvents: {},
    };
  },
  watch: {
    center: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (newValue && this.map && !this.arraysEqual(newValue, oldValue)) {
          if (!this.map.isStyleLoaded()) {
            this.delayUntilStyleIsLoaded(this.map.panTo, [newValue]);
            return;
          }
          this.map.panTo(newValue);
        }
      },
    },
    bounds: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (newValue && this.map && !this.arraysEqual(newValue, oldValue)) {
          if (!this.map.isStyleLoaded()) {
            this.delayUntilStyleIsLoaded(this.fitBounds, [
              newValue,
              this.boundsOptions,
            ]);
            return;
          }
          this.fitBounds(newValue, this.boundsOptions);
        }
      },
    },
    polygons: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (this.map && !this.arraysEqual(newValue, oldValue)) {
          // eslint-disable-next-line
          const me = this;
          const loadPolygons = (polygonsSourceName) => {
            me.removeSourceLayer(polygonsSourceName, polygonsSourceName);
            me.initPolygons();
          };

          if (!this.map.isStyleLoaded()) {
            this.delayUntilStyleIsLoaded(loadPolygons, [me.polygonsSourceName]);
            return;
          }

          loadPolygons(me.polygonsSourceName);
        }
      },
    },
    drawPolygons: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (this.map) {
          if (this.draw) {
            this.draw.deleteAll();
          }
          this.initDrawPolygons();
        }
      },
    },
    drawTools: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (this.map && newValue !== oldValue) {
          if (newValue) {
            if (!this.drawControls) {
              // initialize draw controls
              this.initDrawControls();
            } else {
              this.map.addControl(this.drawControls, this.drawToolsPosition);

              if (this.drawTips) {
                this.map.addControl(this.drawTips, this.drawTipsPosition);
              }
            }
          } else {
            this.map.removeControl(this.drawControls);
            if (this.drawTips) {
              this.map.removeControl(this.drawTips);
            }
          }
        }
      },
    },
    geocoder: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (this.map && newValue !== oldValue) {
          if (newValue) {
            if (!this.geocoderControl) {
              // initialize geocoder
              this.initGeocoder();
            } else {
              if (this.geocoderContainerId) {
                const container = document.getElementById(
                  this.geocoderContainerId
                );

                if (container) {
                  container.appendChild(this.geocoderControl.onAdd(this.map));
                }
              }
            }

            if (this.focusOnGeocoder) {
              this.focusOnGeocoderInput();
            }
          } else {
            this.map.removeControl(this.geocoderControl);
          }
        }
      },
    },
    layersControl: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        if (this.map && newValue !== oldValue) {
          if (newValue) {
            if (!this._layersControl) {
              // initialize layer controls
              this.initLayersControl();
            } else {
              this.map.addControl(
                this._layersControl,
                this.layersControlPosition
              );
            }
          } else {
            this.map.removeControl(this._layersControl);
          }
        }
      },
    },
  },
  mounted() {
    /*
      use setTimeout to let parent get sized
      before initializing the map

      fixes issue of the map tiles loading
      multiple times and causing a sluggish
      load look & feel
      */
    setTimeout(() => {
      this.initMap();
    }, 0);
  },
  beforeMount() {
    this.mapId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  },
  methods: {
    initMap() {
      /*
       * require imports below for map components is
       * workaround to bypass 'ReferencError window is
       * not defined' issue caused by server side
       * rendering w/ nuxt trying to render mapbox
       * component on the server side.
       *
       * more info here:
       *   - https://github.com/mapbox/mapbox-gl-js/issues/4463
       *   - https://gist.github.com/martinrisseeuw/4943a180632e8ec95a45c5aec4061db8
       */

      // eslint-disable-next-line
      const mapboxgl = require("mapbox-gl");
      this.$log.debug(`running mapbox-gl version ${mapboxgl.version}...`);

      // eslint-disable-next-line
      const DualScaleControl = require("mapbox-gl-dual-scale-control");

      // eslint-disable-next-line
      const me = this;

      mapboxgl.accessToken = this.access_token;

      const mapOptions = {
        container: this.mapId,
        style: this.mapStyle,
        attributionControl: this.showAttribution,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        logoPosition: this.logoPosition,
      };

      this.map = new mapboxgl.Map(
        this.bounds
          ? {
              ...mapOptions,
              bounds: this.bounds,
            }
          : { ...mapOptions, center: this.center, zoom: this.zoom }
      );

      if (this.geocoder) {
        this.initGeocoder();
      }

      // since we don't have props to hide coords or scale we need to do it ourselves
      if (this.showScale || this.showCoords) {
        // scale control includes coords
        this.scale = new DualScaleControl({
          maxWidth: 100,
        });
        this.map.addControl(this.scale, this.scaleCoordsPosition);

        if (this.showScale && !this.showCoords) {
          // hide coordinates element
          const el = document.querySelector(
            ".mapboxgl-control-container .mapboxgl-ctrl-scale .map-position"
          );
          el.setAttribute("style", "display:none");
        }

        if (this.showCoords && !this.showScale) {
          // hide scale elements
          const metricEl = document.querySelector(
            ".mapboxgl-control-container .mapboxgl-ctrl-scale .metric-scale"
          );
          const imperialEl = document.querySelector(
            ".mapboxgl-control-container .mapboxgl-ctrl-scale .imperial-scale"
          );
          metricEl.setAttribute("style", "display:none");
          imperialEl.setAttribute("style", "display:none");
        }
      }

      this.map.on("draw.create", function (e) {
        const feature = e.features[0];
        const mode = me.draw.getMode();
        const type =
          mode === "draw_rectangle" ? "Rectangle" : feature.geometry.type;

        const name = me.createUniqueLayerName(type);
        me.draw.setFeatureProperty(feature.id, "name", name);

        // console.log('created: ', e.features, me.draw.getAll());
        me.$emit("drawChange", me.draw.getAll());
        me.$emit("drawCreate", e);
      });

      this.map.on("draw.modechange", (e) => {
        // console.log(`draw.modechange: ${e.mode}`);
        me.$emit("drawModeChange", e.mode);
      });

      this.map.on("draw.delete", function (e) {
        // console.log('deleted: ', e.features);
        me.$emit("drawChange", me.draw.getAll());
        me.$emit("drawDelete", e);
      });

      this.map.on("draw.update", function (e) {
        // console.log('shape edited', e);
        me.$emit("drawChange", me.draw.getAll());
        me.$emit("drawUpdate", e);
      });

      this.map.on("error", (error) => {
        this.$emit("error", me.map, error);
      });

      this.map.on("load", () => {
        me.initPolygons();

        if (me.stylesControl) {
          const mapStylesControl = new MapStylesControl({
            className: "map-styles-ctrl",
            layerIdsToPreserve: me.customLayers,
            imagesToPreserve: me.customImages,
          });
          me.map.addControl(mapStylesControl, this.stylesControlPosition);
          this.mapStylesControl = mapStylesControl;
        }

        if (me.zoomControl) {
          me.map.addControl(
            new mapboxgl.NavigationControl(),
            me.zoomControlPosition
          );
        }

        if (
          me.drawTools ||
          (Array.isArray(me.drawPolygons) && me.drawPolygons.length > 0)
        ) {
          me.initDrawControls();
          me.initDrawPolygons();
        }

        if (me.copyTools.length > 0) {
          const copyTool = new CopyTool({
            copyClassName: "copy_geom",
            geometry: this.copyTools,
          });
          me.map.addControl(copyTool, this.copyToolsPosition);
          me.copyTool = copyTool;
        }

        me.resize();

        if (me.bounds && me.boundsOptions) {
          me.fitBounds(me.bounds, me.boundsOptions);
        }

        if (me.focusOnGeocoder) {
          me.focusOnGeocoderInput();
        }

        if (me.layersControl) {
          me.initLayersControl();
        }

        this.$emit("load", me.map);
      });
    },
    createUniqueLayerName(type) {
      const allLayerNames = [
        ...this.draw
          .getAll()
          .features.filter((f) => f && f.properties && f.properties.name)
          .map((feature) => feature.properties.name),
      ];

      let index = 1;
      const prefix = `${type[0].toUpperCase()}${type.slice(1)}`;

      while (allLayerNames.includes(`${prefix} #${index}`)) {
        index++;
      }

      return `${prefix} #${index}`;
    },
    focusOnGeocoderInput() {
      const geocoderEl = document.querySelector(
        ".mapboxgl-ctrl-geocoder.mapboxgl-ctrl .mapboxgl-ctrl-geocoder--input"
      );

      if (geocoderEl) {
        geocoderEl.focus();
      }
    },
    initPolygons() {
      const data = { type: "FeatureCollection", features: [] };

      if (this.polygons && Array.isArray(this.polygons)) {
        this.polygons.forEach((polygon) => {
          if (polygon) {
            data.features.push(polygon);
          }
        });
      }

      const sourceName = this.polygonsSourceName;
      this.map.addSource(sourceName, { type: "geojson", data });

      const fillLayerId = `${sourceName} Fills`;
      this.map.addLayer({
        id: fillLayerId,
        type: "fill",
        source: sourceName,
        paint: {
          "fill-color": "#3AB1D0", //rgb(58, 177, 208)",
          // "fill-color": "rgb(58, 177, 208)",
          "fill-outline-color": "#3AB1D0",
          "fill-opacity": 0.1,
        },
      });

      const outlineLayerId = `${sourceName} Outlines`;
      this.map.addLayer({
        id: outlineLayerId,
        type: "line",
        source: sourceName,
        layout: {},
        paint: {
          "line-color": "#3AB1D0",
          "line-width": 3,
        },
      });

      if (data.features.length > 0) {
        this.customLayers.push(fillLayerId);
        this.customLayers.push(outlineLayerId);
      }
    },
    initDrawPolygons() {
      if (this.drawTools && this.draw && Array.isArray(this.drawPolygons)) {
        this.drawPolygons.forEach((polygon) => {
          this.draw.add(polygon);
        }, this);
      }
    },
    initDrawControls() {
      // eslint-disable-next-line
      const MapboxDraw = require("@mapbox/mapbox-gl-draw");

      // eslint-disable-next-line
      const DrawRectangle = require("mapbox-gl-draw-rectangle-mode");

      const modes = MapboxDraw.modes;
      modes.draw_rectangle = DrawRectangle.default;

      // eslint-disable-next-line
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        boxSelect: false,
        modes,
        /*
          modes: {
            direct_select: MapboxDraw.modes.direct_select,
            draw_polygon: MapboxDraw.modes.draw_polygon,
            draw_rectangle: DrawRectangle
          }
        */
      });

      this.map.addControl(this.draw, this.drawToolsPosition);

      const drawTips = new DrawTips({
        className: "rect-ctrl",
        draw: this.draw,
      });
      this.map.addControl(drawTips, this.drawTipsPosition);
      this.drawTips = drawTips;

      const drawControls = new DrawControls({
        draw: this.draw,
        selectClassName: "draw_select",
        polygonClassName: "draw_polygon",
        editClassName: "draw_edit",
        deleteVerticesClassName: "draw_delete_vertices",
        onDrawModeChange: (mode, opts) => {
          this.setDrawMode(mode, opts);
          this.$emit("drawModeChange", mode, opts);
        },
      });
      this.map.addControl(drawControls, this.drawToolsPosition);
      this.drawControls = drawControls;
    },
    initGeocoder() {
      // eslint-disable-next-line
      const mapboxgl = require("mapbox-gl");

      // eslint-disable-next-line
      const MapboxGeocoder = require("@mapbox/mapbox-gl-geocoder");

      const geocoder = new MapboxGeocoder({
        accessToken: this.access_token,
        mapboxgl,
        clearAndBlurOnEsc: true,
        marker: false,
        localGeocoder: this.coordinatesGeocoder,
      });

      if (this.geocoderContainerId) {
        const container = document.getElementById(this.geocoderContainerId);
        if (container) {
          container.appendChild(geocoder.onAdd(this.map));
        } else {
          // eslint-disable-next-line
          console.error(
            `Geocoder coult not be added since we not find geocoder container: ${this.geocoderContainerId}`
          );
        }
      } else {
        this.map.addControl(geocoder, this.geocoderPosition);
      }

      this.geocoderControl = geocoder;
    },
    initLayersControl() {
      const layersControl = new LayersControl({
        layers: this.getCustomLayers().reverse(),
        className: "layers_control",
        layersContainerClassName: "layers_control_container",
        titleClassName: "layers_control_title",
        layersListClassName: "layers_control_list_container",
        layerClassName: "layer_container",
      });
      this.map.addControl(layersControl, this.layersControlPosition);
      this._layersControl = layersControl;
    },
    addLayer(layer, beforeId) {
      // console.log(`addLayer(layer=${JSON.stringify(layer)})`);
      this.map.addLayer(layer, beforeId);

      const layerIndex = this.customLayers.indexOf(beforeId);
      if (layerIndex >= 0) {
        this.customLayers.splice(layerIndex, 0, layer.id);
      } else {
        this.customLayers.push(layer.id);
      }

      if (this.mapStylesControl) {
        this.mapStylesControl.updateLayerIdsToPreserve(this.customLayers);
      }

      this.updateLayersControl();
    },
    addSource(sourceName, source) {
      // console.log(`addSource(sourceName=${sourceName})`);
      this.map.addSource(sourceName, source);
    },
    addSourceLayer(sourceName, source, layer, beforeId) {
      this.addSource(sourceName, source);
      this.addLayer(layer, beforeId);
    },
    addSourceLayers(sourceName, source, layers, mapEvents = [], beforeId) {
      // console.log(`addSourceLayers(sourceName=${sourceName})`);
      this.map.addSource(sourceName, source);

      layers.forEach((layer) => {
        // console.log(`adding layer=${JSON.stringify(layer)}`);
        this.map.addLayer(layer, beforeId);

        const layerIndex = this.customLayers.indexOf(beforeId);
        if (layerIndex >= 0) {
          this.customLayers.splice(layerIndex, 0, layer.id);
        } else {
          this.customLayers.push(layer.id);
        }

        if (this.mapStylesControl) {
          this.mapStylesControl.updateLayerIdsToPreserve(this.customLayers);
        }
      }, this);

      if (mapEvents && Array.isArray(mapEvents) && mapEvents.length > 0) {
        const newLayerEvents = {};

        mapEvents.forEach((mapEvent) => {
          // remove all the existing map events for that layerId
          const eventLayersById = this.allLayerEvents[mapEvent.layerId];

          if (
            eventLayersById &&
            Array.isArray(eventLayersById) &&
            eventLayersById.length > 0
          ) {
            // remove all the existing map events for that layerId
            eventLayersById.forEach((layerEvent) => {
              this.map.off(
                layerEvent.type,
                layerEvent.layerId,
                layerEvent.handler
              );
              // console.log( `removed '${layerEvent.type}' type map event for layer w/ id '${layerEvent.layerId}': ${layerEvent.handler}`);
            }, this);

            delete this.allLayerEvents[mapEvent.layerId];
          }

          this.map.on(mapEvent.type, mapEvent.layerId, mapEvent.handler);
          // console.log( `added '${mapEvent.type}' type map event for layer w/ id '${mapEvent.layerId}': ${mapEvent.handler}`);

          if (!newLayerEvents[mapEvent.layerId]) {
            newLayerEvents[mapEvent.layerId] = [];
          }

          newLayerEvents[mapEvent.layerId].push(mapEvent);
        }, this);

        // now add each layerId in newLayerEvents to this components layerEvents tracker
        for (const prop in newLayerEvents) {
          this.allLayerEvents[prop] = newLayerEvents[prop];
        }
      }

      this.updateLayersControl();
    },
    getSource(sourceName) {
      if (this.map) {
        return this.map.getSource(sourceName);
      }

      return null;
    },
    removeLayer(layerId) {
      // console.log( `removeLayer(layerId=${layerId})`);
      const map = this.map;

      try {
        map.removeLayer(layerId);
      } catch (err) {
        // eslint-disable-next-line
        console.error(`Failed to remove layer '${layerId}': ${err}`);
      }

      // remove existing events for this layerId
      const eventLayersById = this.allLayerEvents[layerId];
      if (
        eventLayersById &&
        Array.isArray(eventLayersById) &&
        eventLayersById.length > 0
      ) {
        // remove all the existing map events for that layerId
        eventLayersById.forEach((layerEvent) => {
          map.off(layerEvent.type, layerEvent.layerId, layerEvent.handler);
          // console.log( `removed '${layerEvent.type}' type handler for layer w/ id '${layerEvent.layerId}': ${layerEvent.handler}`);
        });

        delete this.allLayerEvents[layerId];
      }

      const layerIndex = this.customLayers.indexOf(layerId);
      if (layerIndex >= 0) {
        this.customLayers.splice(layerIndex, 1);
        if (this.mapStylesControl) {
          this.mapStylesControl.updateLayerIdsToPreserve(this.customLayers);
        }
      }

      this.updateLayersControl();
    },
    removeSourceLayer(sourceName, layerId) {
      // console.log( `removeSourceLayer(sourceName=${sourceName}, layerId=${layerId})`);
      this.removeLayer(layerId);
      this.removeSource(sourceName);
    },
    removeSourceLayers(sourceName, layerIds) {
      // console.log(`removeSourceLayers(sourceName=${sourceName}, layerIds=${layerIds})`);

      layerIds.forEach((layerId) => {
        this.removeLayer(layerId);
      }, this);
      this.removeSource(sourceName);
    },
    removeSource(sourceName) {
      // console.log(`removeSource(sourceName=${sourceName})`);
      const map = this.map;
      try {
        map.removeSource(sourceName);
      } catch (err) {
        // eslint-disable-next-line
        console.error(`Failed to remove source '${sourceName}': ${err}`);
      }
    },
    updateLayersControl() {
      if (this._layersControl) {
        this._layersControl.updateLayers(this.getCustomLayers().reverse());
      }
    },
    getCustomLayers() {
      // eslint-disable-next-line
      const layers = [];
      this.customLayers.forEach((id) => {
        const layer = this.map.getStyle().layers.find((l) => l.id === id);
        if (layer) {
          layers.push(layer);
        }
      }, this);

      return layers;
    },
    hideLayer(layerId) {
      this.map.setLayoutProperty(layerId, "visibility", "none");
      this.updateLayersControl();
    },
    hideLayers(layerIds) {
      layerIds.forEach((layerId) => {
        this.map.setLayoutProperty(layerId, "visibility", "none");
      });
      this.updateLayersControl();
    },
    showLayer(layerId) {
      this.map.setLayoutProperty(layerId, "visibility", "visible");
      this.updateLayersControl();
    },
    showLayers(layerIds) {
      layerIds.forEach((layerId) => {
        this.map.setLayoutProperty(layerId, "visibility", "visible");
      });
      this.updateLayersControl();
    },
    moveLayer(layerId) {
      this.map.moveLayer(layerId);
    },
    resize() {
      if (this.map) {
        this.map.resize();
      }
    },
    repaint() {
      if (this.map) {
        this.map.triggerRepaint();
      }
    },
    /* given a query in the form "lng, lat" or "lat, lng" returns the matching
     * geographic coordinate(s) as search results in carmen geojson format,
     * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md
     */
    coordinatesGeocoder(query) {
      // match anything which looks like a decimal degrees coordinate pair
      const matches = query.match(
        /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
      );
      if (!matches) {
        return null;
      }

      function coordinateFeature(lng, lat) {
        return {
          center: [lng, lat],
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          place_name: "Lat: " + lat + " Lng: " + lng,
          place_type: ["coordinate"],
          properties: {},
          type: "Feature",
        };
      }

      const coord1 = Number(matches[1]);
      const coord2 = Number(matches[2]);
      const geocodes = [];

      if (coord1 < -90 || coord1 > 90) {
        // must be lng, lat
        geocodes.push(coordinateFeature(coord1, coord2));
      }

      if (coord2 < -90 || coord2 > 90) {
        // must be lat, lng
        geocodes.push(coordinateFeature(coord2, coord1));
      }

      if (geocodes.length === 0) {
        // else could be either lng, lat or lat, lng
        geocodes.push(coordinateFeature(coord1, coord2));
        geocodes.push(coordinateFeature(coord2, coord1));
      }

      return geocodes;
    },
    setDrawMode(mode, opts) {
      // console.log(`setDrawMode::mode=${mode}`);
      if (this.draw) {
        if (mode === "draw_rectangle") {
          this.draw.changeMode("draw_rectangle", opts);
        } else if (mode === "draw_polygon") {
          this.draw.changeMode("draw_polygon", opts);
        } else if (mode === "direct_select") {
          this.draw.changeMode("direct_select", opts);
        } else {
          this.draw.changeMode("simple_select", opts);
        }

        this.drawTips.setMode(mode);
        this.drawControls.setMode(mode);
      }
    },
    addFeatures(features) {
      // console.log(`addFeatures::features=${JSON.stringify(features)})`);

      if (this.draw) {
        for (let k = 0; k < features.length; k++) {
          const feature = features[k];
          const featureIds = this.draw.add(feature);

          if (feature.properties && feature.properties.name) {
            const name = feature.properties.name;

            if (featureIds.length === 1) {
              this.draw.setFeatureProperty(featureIds[0], "name", name);
            } else {
              for (let i = 0; i < featureIds.length; i++) {
                this.draw.setFeatureProperty(
                  featureIds[i],
                  "name",
                  `${name}-${i + 1}`
                );
              }
            }
          }
        }

        this.$emit("drawChange", this.draw.getAll());
      }
    },
    addFeature(feature, name) {
      // console.log(`addFeature::feature=`, feature, `, name=${name}`);
      if (this.draw) {
        const featureIds = this.draw.add(feature);

        if (name) {
          if (featureIds.length === 1) {
            this.draw.setFeatureProperty(featureIds[0], "name", name);
          } else {
            for (let i = 0; i < featureIds.length; i++) {
              this.draw.setFeatureProperty(
                featureIds[i],
                "name",
                `${name}-${i + 1}`
              );
            }
          }
        }

        this.$emit("drawChange", this.draw.getAll());
      }
    },
    removeDrawnFeature(id) {
      if (this.draw) {
        this.draw.delete(id);
        this.$emit("drawChange", this.draw.getAll());
      }
    },
    removeAllDrawnFeatures() {
      if (this.draw) {
        this.draw.deleteAll();
        this.$emit("drawChange", this.draw.getAll());
      }
    },
    setFeatureName(id, name) {
      // console.log(`setFeatureName::id=${id}, name=${name}`);
      this.draw.setFeatureProperty(id, "name", name);
      this.$emit("drawChange", this.draw.getAll());
    },
    fitBounds(bounds, padding) {
      // console.log(
      //   `fitBounds: bounds=${JSON.stringify(bounds)}, padding=${JSON.stringify(
      //     padding
      //   )}`
      // );
      this.map.fitBounds(bounds, padding);
    },
    jumpTo(options) {
      // console.log(`jumpTo: options=${JSON.stringify(options)}`);
      this.map.jumpTo(options);
    },
    flyTo(options) {
      // console.log(`flyTo: options=${JSON.stringify(options)}`);
      this.map.flyTo(options);
    },
    delayUntilStyleIsLoaded(functionToCall, args) {
      if (!this.map.isStyleLoaded()) {
        setTimeout(
          () => this.delayUntilStyleIsLoaded(functionToCall, args),
          250
        );
      } else {
        functionToCall(...args);
      }
    },
    getMapBoxGlMap() {
      return this.map;
    },
    arraysEqual(array1, array2) {
      return JSON.stringify(array1) === JSON.stringify(array2);
    },
    clear() {
      // removes everything from the drawn layer
      this.removeAllDrawnFeatures();

      const polygonsSource = this.getSource(this.polygonsSourceName);

      if (polygonsSource) {
        this.getSource(this.polygonsSourceName).setData({
          type: "FeatureCollection",
          features: [],
        });
      }
    },
  },

  unmounted() {
    // console.log('unmounting map');
    if (this.map) {
      const map = this.map;

      // remove leftover events for each layer
      for (const prop in this.allLayerEvents) {
        const eventsForLayer = this.allLayerEvents[prop];
        if (Array.isArray(eventsForLayer)) {
          // remove all the existing events for that layerId
          eventsForLayer.forEach((layerEvent) => {
            map.off(layerEvent.type, layerEvent.layerId, layerEvent.handler);
            // console.log( `removed '${layerEvent.type}' type event for layer w/ id '${layerEvent.layerId}': ${layerEvent.handler}`);
          });
        }

        delete this.allLayerEvents[prop];
      }

      // TODO delete sources and layers???
      this.map.remove();
      this.map = null;
    }
  },
};
</script>

<style>
.mapboxgl-control-container .mapboxgl-ctrl-scale {
  border: none;
  background-color: transparent;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  padding: 0;
}
.mapboxgl-control-container .mapboxgl-ctrl-top-left .mapboxgl-ctrl-scale,
.mapboxgl-control-container .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-scale {
  align-items: flex-start;
}
.mapboxgl-control-container .mapboxgl-ctrl-scale .map-position {
  border: none;
}
.mapboxgl-control-container .mapboxgl-ctrl-scale .map-position,
.mapboxgl-control-container .mapboxgl-ctrl-scale .metric-scale,
.mapboxgl-control-container .mapboxgl-ctrl-scale .imperial-scale {
  background-color: hsla(0, 0%, 100%, 0.5);
  padding: 0 5px;
  margin-top: 5px;
}
.mapboxgl-control-container .mapboxgl-ctrl-scale .metric-scale,
.mapboxgl-control-container .mapboxgl-ctrl-scale .imperial-scale {
  border: 2px solid #333;
  border-top: none;
  margin-top: 2px;
}
.map-styles-ctrl {
  border: none;
  padding: 0 5px;
}
.rect-ctrl {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px;
  max-width: 50%;
}
.draw_select {
  background-image: url(data:image/png;base64,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);
  background-size: 16px;
}
.draw_edit {
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAABmJLR0QAAAAAAAD5Q7t/AAAACXBIWXMAAABgAAAAYADwa0LPAAAAhUlEQVRIx+2TOwqAMBAFx8v5v4ReQwtvYO8dUxsL0SaBENTCrIqQBw9SzSzLBmJeSA4oYAQSaXgGzMBmOklLegduO0qAS6A178ETKAm4BtYDyWzWdjuFgdtpXUknDXclTeha/Gtxq4Eqwv8FT7+E1yFwLuDBP9TmscnPBIsk3BeIw2MOswPW6WxowEPwSAAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyMi0wMy0wM1QxOTowODo0OCswMDowMBa6dt4AAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjItMDMtMDNUMTk6MDg6NDgrMDA6MDBn585iAAAAAElFTkSuQmCC);
  background-size: 16px;
}
.draw_polygon {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSIyNSIgdmlld0JveD0iMCAwIDI1IDI1Ij48ZGVmcz48c3R5bGU+LmF7ZmlsbDojMzczNjRkO308L3N0eWxlPjwvZGVmcz48ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwIDApIj48cGF0aCBjbGFzcz0iYSIgZD0iTTkuMzc1LDBBMi4wODIsMi4wODIsMCwwLDAsNy4yOTIsMi4wODMsMi4xMDgsMi4xMDgsMCwwLDAsNy42NSwzLjI1NWwtNC4xLDYuMTUyYTIuMDc0LDIuMDc0LDAsMCwwLS40MjMtLjAzMywyLjA4MiwyLjA4MiwwLDAsMC0uOTQ0LDMuOTM5bC0uNzE2LDcuNjE3QTIuMDgsMi4wOCwwLDEsMCw0LDIzLjdoOS43YTIuMDc0LDIuMDc0LDAsMSwwLDMuNzExLTEuODIzbDQuOTQ4LTYuMzE1YTIuMDg4LDIuMDg4LDAsMSwwLC41NTMtNC4xLDIuMDkzLDIuMDkzLDAsMCwwLS4zNTguMDMzTDE5LjQsNi40NDVhMi4xMiwyLjEyLDAsMCwwLC4zOTEtMS4yMzcsMi4wNzQsMi4wNzQsMCwwLDAtMy42MTMtMS40bC00LjcyLTEuNzU4QTIuMDgzLDIuMDgzLDAsMCwwLDkuMzc1LDBaTTEwLjksMy40ODNsNC43MiwxLjc1OGEyLjA4MywyLjA4MywwLDAsMCwyLjA4MywyLjA1MSwyLjA1MywyLjA1MywwLDAsMCwuMzkxLS4wMzNMMjEuMjU3LDEyLjNhMi4wMSwyLjAxLDAsMCwwLS4xMywyLjI3OUwxNi4xNzgsMjAuOUEyLjExNSwyLjExNSwwLDAsMCwxMy43LDIyLjEzNUg0YTIuMDY2LDIuMDY2LDAsMCwwLS45NzctMS4wNzRsLjY4NC03LjYxN2EyLjA3NiwyLjA3NiwwLDAsMCwxLjUtMS45ODYsMi4xMDgsMi4xMDgsMCwwLDAtLjM1OC0xLjE3Mmw0LjEtNi4xNTJBMi4xNTksMi4xNTksMCwwLDAsMTAuOSwzLjQ4M1oiLz48L2c+PC9zdmc+);
  background-size: 16px;
}
.copy_geom {
  background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48IURPQ1RZUEUgc3ZnIFBVQkxJQyAiLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4iICJodHRwOi8vd3d3LnczLm9yZy9HcmFwaGljcy9TVkcvMS4xL0RURC9zdmcxMS5kdGQiPjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTE5LDIxSDhWN0gxOU0xOSw1SDhBMiwyIDAgMCwwIDYsN1YyMUEyLDIgMCAwLDAgOCwyM0gxOUEyLDIgMCAwLDAgMjEsMjFWN0EyLDIgMCAwLDAgMTksNU0xNiwxSDRBMiwyIDAgMCwwIDIsM1YxN0g0VjNIMTZWMVoiIC8+PC9zdmc+);
  background-size: 16px;
}
.draw_delete_vertices {
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAABmJLR0QAAAAAAAD5Q7t/AAAACXBIWXMAAABgAAAAYADwa0LPAAAAo0lEQVRIx+2UXQqDMAzHf4765h4GvYXe/xS+7Bweor5kI4ptQzUDYX8oSdp8JxTujk7xyaBzhKLdw7uCUMg47ai1Gxt99wruP4NcRta+V+3cK/gHqCKcd/HF06rYukWHuKJFIxCVHOUum/n+lDABC/AWx1H4Rd5OB/g4TEI1H3MBagETMCi9FzCrt43z1hnoDzAAvZJ7Mtv5sxa5DbkV5jV1wQokmUWhrvL1ZgAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyMi0wMy0wM1QxOToxMzoyOSswMDowME/MUNoAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjItMDMtMDNUMTk6MTM6MjkrMDA6MDA+kehmAAAAAElFTkSuQmCC);
  background-size: 16px;
}

.geohash_filter_container {
  background-color: rgba(255, 255, 255, 1);
  padding: 0.5rem 0.8rem;
}

.geohash_filter_categories_container {
  margin: 0.4rem 0;
}

.layers_control_list_container {
  margin: 1rem 0 0.4rem 0;
}

.layer_container {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
}

.layer_container[disabled] div {
  color: #6c757d;
}

.layer_container[disabled] div button:first-child i {
  color: #6c757d;
}

.geohash_filter_category_container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin: 0.2rem 0;
}

.geohash_filter_category_container:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.geohash_filter_clear_filter_container {
  margin-bottom: 0.4rem;
  text-align: right;
}

.geohash_filter_category_container[disabled] {
  opacity: 0.35;
}

.geohash_filter_category_color {
  height: 1.2rem;
  width: 1.2rem;
}

.geohash_filter_category_label {
  font-size: 0.7rem;
  margin-left: 0.5rem;
}

.geohash_filter_total {
  text-align: right;
}

.geohash_filter_total,
.layers_control_title {
  font-weight: 500;
  font-size: 0.9rem;
}

.geohash_filter_total_selected_category_container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.geohash_filter_total_selected_category_container div:first-child {
  margin-right: 0.4rem;
  width: 1.2rem;
  height: 1.2rem;
}

.layers_control {
  background-color: rgba(255, 255, 255, 1);
  color: black;
}

.layers_control_container {
  padding: 0.5rem 0.8rem;
}
</style>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.map {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  background-color: #f5f6fa;
}
</style>
